import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, timer, forkJoin } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, retry, tap, filter } from 'rxjs/operators';
import * as EquipmentActions from './equipment.actions';
import { EquipmentService } from '../../../../core/services/gse/equipment.service';
import { EquipmentCategoryService } from '../../../../core/services/gse/equipment-category.service';
import { MaintenanceDashboardService } from '../../../../core/services/gse/maintenance-dashboard.service';
import { EquipmentResponse } from '../../../../api/gse/models/equipment-response';
import { StrictHttpResponse } from '../../../../api/gse/strict-http-response';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../../core/constants/platform-config.constants';
import { ToastActions } from '../../../toast/toast.actions';
import { Equipment } from '../../../../features/gse/inventory/equipment/pages/equipment-dashboard/equipment.types';
import { EquipmentListResponse } from '../../../../core/services/gse/equipment.service';
import { AlertTypes } from '../../../../api/gse/models/alert-types';

@Injectable()
export class EquipmentEffects {
  private actions$ = inject(Actions);
  private equipmentService = inject(EquipmentService);
  private router = inject(Router);
  private equipmentCategoryService = inject(EquipmentCategoryService);
  private maintenanceDashboardService = inject(MaintenanceDashboardService);

  loadEquipments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipments),
      mergeMap(({ params }) =>
        this.equipmentService.loadEquipments(params).pipe(
          map((response: EquipmentListResponse) =>
            EquipmentActions.loadEquipmentsSuccess({
              data: response.data,
              totalRecords: response.totalRecords,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadEquipmentsFailure({
                error: error?.error?.message || 'Failed to load equipments',
              })
            )
          )
        )
      )
    )
  );

  loadEquipment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipment),
      mergeMap(({ id }) =>
        this.equipmentService.getEquipmentById(id).pipe(
          map((equipment: Equipment | undefined) => {
            if (equipment) {
              return EquipmentActions.loadEquipmentSuccess({ equipment });
            }
            return EquipmentActions.loadEquipmentFailure({ error: 'Equipment not found' });
          }),
          catchError((error) => {
            const errorMsg =
              error.status === 404
                ? 'Equipment not found'
                : error?.error?.message || 'Failed to load equipment details';
            return of(EquipmentActions.loadEquipmentFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  createEquipment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.createEquipment),
      mergeMap((action) =>
        this.equipmentService.createEquipment(action.body).pipe(
          map((response: StrictHttpResponse<EquipmentResponse>) =>
            EquipmentActions.createEquipmentSuccess({
              response: response.body,
              images: action.images,
              attachments: action.attachments,
              returnTo: action.returnTo,
              previousReturnTo: action.previousReturnTo,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.createEquipmentFailure({
                error: error?.error?.message || 'Failed to create equipment',
              })
            )
          )
        )
      )
    )
  );
  updateEquipment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.updateEquipment),
      mergeMap((action) =>
        this.equipmentService.updateEquipment(action.id, action.body).pipe(
          mergeMap((response: StrictHttpResponse<EquipmentResponse>) => {
            const equipmentId = response.body?.data?.id;

            if (
              equipmentId &&
              action.deleteAttachmentUuids &&
              action.deleteAttachmentUuids.length > 0
            ) {
              return forkJoin(
                action.deleteAttachmentUuids.map((uuid) =>
                  this.equipmentService.deleteAttachment(equipmentId, uuid).pipe(
                    retry({ count: 2, delay: 500 }),
                    catchError(() => of(null)) // don't break whole flow
                  )
                )
              ).pipe(
                map(() =>
                  EquipmentActions.updateEquipmentSuccess({
                    response: response.body,
                    images: action.images,
                    attachments: action.attachments,
                    deleteAttachmentUuids: [],
                    returnTo: action.returnTo,
                    previousReturnTo: action.previousReturnTo,
                  })
                )
              );
            }

            // If no attachments to delete
            return of(
              EquipmentActions.updateEquipmentSuccess({
                response: response.body,
                images: action.images,
                attachments: action.attachments,
                deleteAttachmentUuids: [],
                returnTo: action.returnTo,
                previousReturnTo: action.previousReturnTo,
              })
            );
          }),
          catchError((error) =>
            of(
              EquipmentActions.updateEquipmentFailure({
                error: error?.error?.message || 'Failed to update equipment',
              })
            )
          )
        )
      )
    )
  );

  triggerUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.createEquipmentSuccess, EquipmentActions.updateEquipmentSuccess),
      map(
        (
          action:
            | ReturnType<typeof EquipmentActions.createEquipmentSuccess>
            | ReturnType<typeof EquipmentActions.updateEquipmentSuccess>
        ) => {
          const { response, images, attachments, returnTo, previousReturnTo } = action;
          const id = response.data?.id;
          if (id && ((images && images.length > 0) || (attachments && attachments.length > 0))) {
            return EquipmentActions.uploadFiles({
              id,
              images: images || [],
              attachments: attachments || [],
              returnTo,
              previousReturnTo,
            });
          }
          return { type: 'NOOP' };
        }
      )
    )
  );

  deleteAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.updateEquipmentSuccess),
      mergeMap(({ response, deleteAttachmentUuids }) => {
        const id = response.data?.id;
        if (id && deleteAttachmentUuids && deleteAttachmentUuids.length > 0) {
          return forkJoin(
            deleteAttachmentUuids.map((uuid) =>
              this.equipmentService.deleteAttachment(id, uuid).pipe(
                retry({ count: 2, delay: 500 }),
                catchError((error) => {
                  return of(null);
                })
              )
            )
          ).pipe(map(() => ({ type: 'NOOP' })));
        }
        return of({ type: 'NOOP' });
      })
    )
  );

  uploadFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.uploadFiles),
      mergeMap((action) =>
        this.equipmentService
          .uploadEquipmentFiles(action.id, action.images, action.attachments)
          .pipe(
            map((response: EquipmentResponse) =>
              EquipmentActions.uploadFilesSuccess({
                response,
                returnTo: action.returnTo,
                previousReturnTo: action.previousReturnTo,
              })
            ),
            retry({
              count: 3,
              delay: () => timer(3000),
            }),
            catchError((error) =>
              of(
                EquipmentActions.uploadFilesFailure({
                  error: error?.error?.message || 'Failed to upload files',
                  returnTo: action.returnTo,
                  previousReturnTo: action.previousReturnTo,
                })
              )
            )
          )
      )
    )
  );

  handleSubmissionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EquipmentActions.createEquipmentSuccess,
          EquipmentActions.updateEquipmentSuccess,
          EquipmentActions.uploadFilesSuccess
        ),
        filter(
          (
            action:
              | ReturnType<typeof EquipmentActions.createEquipmentSuccess>
              | ReturnType<typeof EquipmentActions.updateEquipmentSuccess>
              | ReturnType<typeof EquipmentActions.uploadFilesSuccess>
          ) => {
            if (
              action.type === EquipmentActions.createEquipmentSuccess.type ||
              action.type === EquipmentActions.updateEquipmentSuccess.type
            ) {
              const successAction = action;
              const files =
                (successAction.images?.length || 0) + (successAction.attachments?.length || 0);
              return files === 0;
            }
            return true;
          }
        ),
        tap(
          ({ response, returnTo, previousReturnTo }) => {
            const equipmentId = response?.data?.id;

            if (equipmentId) {
              // Determine which page the details page should go back to
              // If we came from details (via edit), keep the original source (previousReturnTo)
              // If we came from elsewhere, go back there
              const finalReturnTo = returnTo === 'details' ? previousReturnTo : returnTo;

              this.router.navigate(
                ['/', APP_PATHS.GSE, APP_PATHS.INVENTORY, APP_PATHS.EQUIPMENT, equipmentId],
                {
                  state: { returnTo: finalReturnTo || 'listing' },
                }
              );
            } else {
              // Fallback if no ID available (unlikely on success)
              this.router.navigate(['/', APP_PATHS.GSE, APP_PATHS.INVENTORY, APP_PATHS.EQUIPMENT]);
            }
          }
        ),
        map(({ response }) =>
          ToastActions.showSuccessToast({
            summary: 'Success',
            detail: response?.message,
          })
        )
      ),
    { dispatch: true }
  );

  handleSubmissionPartialSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EquipmentActions.uploadFilesFailure),
        tap(() => {
          // On file upload failure, navigate back to equipment listing.
          this.router.navigate(['/', APP_PATHS.GSE, APP_PATHS.INVENTORY, APP_PATHS.EQUIPMENT]);
        }),
        map(() =>
          ToastActions.showWarnToast({
            summary: 'Partial Success',
            detail: 'Equipment added successfully, but image and attachment upload fails',
          })
        )
      ),
    { dispatch: true }
  );

  handleSubmissionFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EquipmentActions.createEquipmentFailure, EquipmentActions.updateEquipmentFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error || 'Failed to save equipment',
          })
        )
      ),
    { dispatch: true }
  );

  // Dashboard Effects
  loadEquipmentDashboardSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentDashboardSummary),
      mergeMap(() =>
        this.equipmentService.getEquipmentDashboardSummary().pipe(
          map((response) =>
            EquipmentActions.loadEquipmentDashboardSummarySuccess({
              data: response.body.data!,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadEquipmentDashboardSummaryFailure({
                error: error?.error?.message || 'Failed to load dashboard summary',
              })
            )
          )
        )
      )
    )
  );

  loadEquipmentStatusChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentStatusChart),
      mergeMap(() =>
        this.equipmentService.getEquipmentStatusChart().pipe(
          map((response) =>
            EquipmentActions.loadEquipmentStatusChartSuccess({
              data: response.body.data!,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadEquipmentStatusChartFailure({
                error: error?.error?.message || 'Failed to load status chart',
              })
            )
          )
        )
      )
    )
  );

  loadMaintenanceWeeklyOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadMaintenanceWeeklyOverview),
      mergeMap(({ params }) =>
        this.maintenanceDashboardService.getMaintenanceWeeklyOverview(params).pipe(
          map((data) =>
            EquipmentActions.loadMaintenanceWeeklyOverviewSuccess({
              data,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadMaintenanceWeeklyOverviewFailure({
                error: error?.error?.message || 'Failed to load maintenance weekly overview',
              })
            )
          )
        )
      )
    )
  );

  loadCriticalAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadCriticalAlerts),
      mergeMap(({ params }) =>
        this.maintenanceDashboardService
          .getMaintenanceAlerts({
            ...params,
            type: 'CRITICAL',
            size: 20,
          })
          .pipe(
            map((response) =>
              EquipmentActions.loadCriticalAlertsSuccess({
                data: response.data,
              })
            ),
            catchError((error) =>
              of(
                EquipmentActions.loadCriticalAlertsFailure({
                  error: error?.error?.message || 'Failed to load critical alerts',
                })
              )
            )
          )
      )
    )
  );

  loadUpcomingMaintenances$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadUpcomingMaintenances),
      mergeMap(({ params }) =>
        this.maintenanceDashboardService
          .getMaintenanceAlerts({
            ...params,
            type: 'UPCOMING' as AlertTypes,
            size: 20,
          })
          .pipe(
            map((response) =>
              EquipmentActions.loadUpcomingMaintenancesSuccess({
                data: response.data,
              })
            ),
            catchError((error) =>
              of(
                EquipmentActions.loadUpcomingMaintenancesFailure({
                  error: error?.error?.message || 'Failed to load upcoming maintenances',
                })
              )
            )
          )
      )
    )
  );

  handleDashboardFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EquipmentActions.loadEquipmentDashboardSummaryFailure,
          EquipmentActions.loadEquipmentStatusChartFailure
        ),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error || 'Failed to load dashboard data',
          })
        )
      ),
    { dispatch: true }
  );

  // Manufacturer Lookup Effect
  loadManufacturerLookups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadManufacturerLookups),
      mergeMap((action) =>
        this.equipmentService
          .getManufacturerLookups(action.search, action.page, action.size, action.status)
          .pipe(
            map((manufacturers) =>
              EquipmentActions.loadManufacturerLookupsSuccess({ manufacturers })
            ),
            catchError((error) =>
              of(
                EquipmentActions.loadManufacturerLookupsFailure({
                  error: error?.error?.message || 'Failed to load manufacturer lookups',
                })
              )
            )
          )
      )
    )
  );

  loadCategoryLookups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadCategoryLookups),
      mergeMap((action) =>
        this.equipmentService
          .getCategoryLookups(
            action.search,
            action.manufacturerId,
            action.status,
            action.page,
            action.size
          )
          .pipe(
            map((categories) => EquipmentActions.loadCategoryLookupsSuccess({ categories })),
            catchError((error) =>
              of(
                EquipmentActions.loadCategoryLookupsFailure({
                  error: error?.error?.message || 'Failed to load category lookups',
                })
              )
            )
          )
      )
    )
  );

  // Equipment Types (Category list) Effect
  loadEquipmentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentTypes),
      mergeMap(({ params }) =>
        this.equipmentCategoryService.getCategories(params).pipe(
          map((response) =>
            EquipmentActions.loadEquipmentTypesSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements ?? (response.data?.length || 0),
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadEquipmentTypesFailure({
                error: error?.error?.message || 'Failed to load equipment types',
              })
            )
          )
        )
      )
    )
  );

  // Model Lookup Effect
  loadModelLookups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadModelLookups),
      mergeMap((action) =>
        this.equipmentService
          .getModelLookups(
            action.search,
            action.manufacturerId,
            action.categoryId,
            action.page,
            action.size,
            action.status
          )
          .pipe(
            map((models) => EquipmentActions.loadModelLookupsSuccess({ models })),
            catchError((error) =>
              of(
                EquipmentActions.loadModelLookupsFailure({
                  error: error?.error?.message || 'Failed to load model lookups',
                })
              )
            )
          )
      )
    )
  );

  // Equipment Logs Effect
  loadEquipmentLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentLogs),
      mergeMap((action) =>
        this.equipmentService.getEquipmentLogs(action.id, action.next, action.size, action.sort).pipe(
          map((response) =>
            EquipmentActions.loadEquipmentLogsSuccess({
              data: response.data || [],
              next: response.next,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadEquipmentLogsFailure({
                error: error?.error?.message || 'Failed to load equipment logs',
              })
            )
          )
        )
      )
    )
  );

  // Load More Equipment Logs Effect (cursor-based pagination)
  loadMoreEquipmentLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadMoreEquipmentLogs),
      mergeMap((action) =>
        this.equipmentService.getEquipmentLogs(action.id, action.next, action.size, action.sort).pipe(
          map((response) =>
            EquipmentActions.loadMoreEquipmentLogsSuccess({
              data: response.data || [],
              next: response.next,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadMoreEquipmentLogsFailure({
                error: error?.error?.message || 'Failed to load more equipment logs',
              })
            )
          )
        )
      )
    )
  );

  // Equipment Maintenance Logs Effect
  loadEquipmentMaintenanceLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadEquipmentMaintenanceLogs),
      mergeMap((action) =>
        this.equipmentService.getEquipmentMaintenanceLogs(action.id, action.cursor, action.size).pipe(
          map((response) =>
            EquipmentActions.loadEquipmentMaintenanceLogsSuccess({
              data: response.data || [],
              next: response.next,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadEquipmentMaintenanceLogsFailure({
                error: error?.error?.message || 'Failed to load equipment maintenance logs',
              })
            )
          )
        )
      )
    )
  );

  // Load More Equipment Maintenance Logs Effect (cursor-based pagination)
  loadMoreEquipmentMaintenanceLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EquipmentActions.loadMoreEquipmentMaintenanceLogs),
      exhaustMap((action) =>
        this.equipmentService.getEquipmentMaintenanceLogs(action.id, action.cursor, action.size).pipe(
          map((response) =>
            EquipmentActions.loadMoreEquipmentMaintenanceLogsSuccess({
              data: response.data || [],
              next: response.next,
            })
          ),
          catchError((error) =>
            of(
              EquipmentActions.loadMoreEquipmentMaintenanceLogsFailure({
                error: error?.error?.message || 'Failed to load more equipment maintenance logs',
              })
            )
          )
        )
      )
    )
  );
}
